import { useForm } from 'react-hook-form';
import { getRegisterFormSchema } from './LaunchingRegisterForm.schema';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  RegisterFormProps,
  RegisterFormSchema,
  RegisterFormVariants,
  RegisterMutation,
  RegisterMutationVariables,
} from './LaunchingRegisterForm.types';
import { registerFormData } from './LaunchingRegisterForm.data';
import { useMemo, useState } from 'react';
import Form from '../../../../commons/Form/Form';
import { UserRoles } from '../../../../../types/models/user/user.types';
import { useMutation, useQueryClient } from 'react-query';
import { REGISTER_QUERY_KEY, RegisterQuery } from './LaunchingRegisterForm.query';
import moment from 'moment';
import departments from '../../../../../assets/json/departements-region.json';
import { LaunchingRegisterModal } from '../LaunchingRegisterModal/LaunchingRegisterModal';
import { useFormationSessions } from '../../../../../hooks/fetchers/useFormationSessions/useFormationSessions';

export default ({ variant }: RegisterFormProps) => {
  const queryClient = useQueryClient();

  const [isOpen, setOpen] = useState(false);

  const formProps = useForm<RegisterFormSchema>({
    resolver: yupResolver(getRegisterFormSchema[variant]),
    mode: 'onChange',
    reValidateMode: 'onChange',
    ...(variant === RegisterFormVariants.AcheteurPublicPhysique && {
      defaultValues: {
        persons: 1,
      },
    }),
  });

  const { handleSubmit } = formProps;

  const { data: formationsData } = useFormationSessions({
    enabled: variant === RegisterFormVariants.CommercialFormation,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const { mutate, isLoading, isSuccess } = useMutation<
    Awaited<ReturnType<RegisterMutation>>,
    Error,
    RegisterMutationVariables
  >((user) => RegisterQuery({ variant, user }), {
    mutationKey: REGISTER_QUERY_KEY,
    onMutate: () => queryClient.cancelMutations(),
    onSuccess: async () => {
      setOpen(true);
    },
    onError: async (error) => alert(error.message),
  });

  const currentVariantRole = useMemo(() => {
    switch (variant) {
      case RegisterFormVariants.AcheteurPublicPhysique:
        return UserRoles.AcheteurPublic;
      case RegisterFormVariants.AcheteurPublicLive:
        return UserRoles.AcheteurPublic;
      case RegisterFormVariants.CommercialFormation:
        return UserRoles.Commercial;
    }
  }, [variant]);

  const onSubmit = handleSubmit((data) => mutate({ ...data, role: currentVariantRole }));

  const currentFormSchema = getRegisterFormSchema[variant];

  const currentFormData = registerFormData.map((row) =>
    row.filter((item) => {
      if (currentFormSchema['_nodes'].includes(item.name)) {
        return item;
      }
    })
  );

  const updatedFormData = useMemo(
    () =>
      currentFormData.map((row) =>
        row.map((item) => {
          switch (item.name) {
            case 'formations':
              return {
                ...item,
                options: formationsData?.map(({ id, start, end }) => ({
                  label: `${moment(start).format('DD/MM/Y')} - ${moment(end).format('DD/MM/Y')}`,
                  value: id.toString(),
                })),
              };
            case 'department':
              return {
                ...item,
                options: (departments as any[]).map((department) => ({
                  label: department.dep_name,
                  value: department.num_dep,
                })),
              };
            default:
              return item;
          }
        })
      ),
    [currentFormData, formationsData]
  );

  return (
    <>
      <Form<RegisterFormSchema> data={updatedFormData} {...{ formProps, onSubmit }} />
      <LaunchingRegisterModal {...{ isOpen, setOpen, isLoading, isSuccess }} />
    </>
  );
};
